import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { type SubscriptionPlan, type PlanDetails } from '../types/subscription';
import { useSubscription } from '../hooks/useSubscription';
import { useInfluencer } from '../contexts/InfluencerContext';
import CancelSubscriptionModal from './CancelSubscriptionModal';
import CancelSuccessModal from './CancelSuccessModal';
import ActiveSubscriptionModal, { type ActiveSubscriptionInfo } from './ActiveSubscriptionModal';
import UpgradeSuccessModal from './UpgradeSuccessModal';

interface UpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentPlan: SubscriptionPlan;
  requiredFeature?: string;
  highlightPlan?: SubscriptionPlan;
  returnContext?: string; // Context to return to after successful subscription
}

const UpgradeModal: React.FC<UpgradeModalProps> = ({
  isOpen,
  onClose,
  currentPlan,
  requiredFeature,
  highlightPlan,
  returnContext,
}) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<SubscriptionPlan | null>(highlightPlan || null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [currentStep, setCurrentStep] = useState<'plans' | 'features' | 'checkout'>('plans');
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);
  const [isCancelSuccessOpen, setIsCancelSuccessOpen] = useState(false);
  const [cancelledPlanName, setCancelledPlanName] = useState<string>('');
  const [isActiveSubModalOpen, setIsActiveSubModalOpen] = useState(false);
  const [activeSubscriptionInfo, setActiveSubscriptionInfo] = useState<ActiveSubscriptionInfo | null>(null);
  const [isUpgradeSuccessOpen, setIsUpgradeSuccessOpen] = useState(false);
  const [upgradeInfo, setUpgradeInfo] = useState<{ oldPlan: string; newPlan: string } | null>(null);
  const [checkoutInfo, setCheckoutInfo] = useState<{
    isTestMode: boolean;
    trialPeriodDays: number | null;
  } | null>(null);
  const [discountCode, setDiscountCode] = useState<string>('');
  const [discountInfo, setDiscountInfo] = useState<{
    valid: boolean;
    code: string;
    name: string;
    percentOff: number | null;
    amountOff: number | null;
    duration?: string;
    durationInMonths?: number | null;
    error?: string;
  } | null>(null);
  const [isValidatingDiscount, setIsValidatingDiscount] = useState(false);
  const checkoutInfoFetchedRef = React.useRef(false);
  const { getToken } = useAuth();
  const { availablePlans, isPlansLoading, plan: subscriptionPlan, expiresAt, cancelAtPeriodEnd, refreshSubscription } = useSubscription();
  const { discountCode: influencerDiscountCode, influencer } = useInfluencer();
  
  // Use subscription plan from hook, then fall back to prop
  const activePlan = subscriptionPlan || currentPlan;
  
  // Use available plans or empty array while loading
  const plans = availablePlans.length > 0 ? availablePlans : [];

  useEffect(() => {
    if (isOpen) {
      setCurrentStep('plans');
      setSelectedPlan(highlightPlan || null);
      checkoutInfoFetchedRef.current = false; // Reset when modal opens
      // Auto-apply influencer discount code if available
      if (influencerDiscountCode) {
        setDiscountCode(influencerDiscountCode);
      } else {
        setDiscountCode('');
      }
      setDiscountInfo(null);
    }
  }, [isOpen, highlightPlan, influencerDiscountCode]);

  // Fetch checkout info when entering checkout step
  useEffect(() => {
    const fetchCheckoutInfo = async () => {
      if (currentStep === 'checkout' && !checkoutInfoFetchedRef.current) {
        checkoutInfoFetchedRef.current = true; // Mark as fetched immediately to prevent duplicate calls
        try {
          const token = await getToken();
          if (!token) return;

          const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
          const response = await fetch(`${apiUrl}/api/billing/checkout-info`, {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${token}`,
            },
          });

          if (response.ok) {
            const data = await response.json();
            setCheckoutInfo({
              isTestMode: data.isTestMode || false,
              trialPeriodDays: data.trialPeriodDays || null,
            });
          }
        } catch (error) {
          console.error('[UpgradeModal] Failed to fetch checkout info:', error);
        }
      }
    };

    fetchCheckoutInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  if (!isOpen) return null;

  const getFeatureMessage = () => {
    const messages: Record<string, string> = {
      replay: 'Replay Mode lets you practice trading with historical data',
      tickReplay: 'Tick-by-tick replay provides the most realistic trading simulation',
      multiChart: 'Multi-chart layouts help you analyze multiple timeframes simultaneously',
      advancedAnalysis: 'Advanced AI analysis provides deeper market insights',
      api: 'API access enables custom integrations and automated trading',
      aiAnalysis: 'AI Guru analysis and live chat provides intelligent trading insights and personalized guidance',
    };
    return requiredFeature ? messages[requiredFeature] : '';
  };

  const getPlanHierarchy = (plan: SubscriptionPlan): number => {
    const hierarchy: Record<SubscriptionPlan, number> = { free: 0, essential: 1, plus: 2, premium: 3 };
    return hierarchy[plan];
  };

  const canUpgradeTo = (plan: SubscriptionPlan): boolean => {
    return getPlanHierarchy(plan) > getPlanHierarchy(activePlan);
  };

  const canChangeTo = (plan: SubscriptionPlan): boolean => {
    // Allow changing to any plan that's different from current plan (upgrade or downgrade)
    return plan !== activePlan && plan !== 'free';
  };

  const handleSelectPlan = (plan: SubscriptionPlan) => {
    if (canChangeTo(plan)) {
      setSelectedPlan(plan);
    }
  };

  const handleContinue = () => {
    if (currentStep === 'plans' && selectedPlan) {
      setCurrentStep('features');
    } else if (currentStep === 'features') {
      setCurrentStep('checkout');
    }
  };

  const handleBack = () => {
    if (currentStep === 'features') {
      setCurrentStep('plans');
    } else if (currentStep === 'checkout') {
      setCurrentStep('features');
    }
  };

  const handleCheckout = async () => {
    if (!selectedPlan || selectedPlan === 'free') return;

    setIsProcessing(true);
    try {
      // Find the selected plan details
      const plan = plans.find(p => p.id === selectedPlan);
      if (!plan) {
        console.error('[UpgradeModal] Plan not found:', selectedPlan);
        return;
      }

      // Get the price ID based on billing period
      const priceId = billingPeriod === 'yearly' ? plan.clerkPriceIdYearly : plan.clerkPriceId;
      
      console.log('[UpgradeModal] Checkout Debug:', {
        selectedPlan,
        billingPeriod,
        plan: {
          id: plan.id,
          name: plan.name,
          clerkPriceId: plan.clerkPriceId,
          clerkPriceIdYearly: plan.clerkPriceIdYearly,
          price: plan.price,
          priceYearly: plan.priceYearly
        },
        selectedPriceId: priceId
      });
      
      if (!priceId) {
        console.error('[UpgradeModal] No price ID found for plan:', selectedPlan, billingPeriod);
        alert('No price ID configured for this plan. Please contact support.');
        return;
      }

      // Get auth token for authenticated API call
      const token = await getToken();
      if (!token) {
        console.error('[UpgradeModal] No auth token available');
        alert('Authentication required. Please sign in again.');
        return;
      }

      // Call backend to create checkout session
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const checkoutEndpoint = `${apiUrl}/api/billing/checkout`;
      
      console.log('[UpgradeModal] Creating checkout session:', {
        endpoint: checkoutEndpoint,
        priceId,
        feature: requiredFeature,
        returnContext
      });
      
      const response = await fetch(checkoutEndpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ 
          price_id: priceId,
          feature: requiredFeature,
          return_context: returnContext,
          discount_code: discountInfo?.valid ? discountCode : undefined,
        }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        console.error('[UpgradeModal] Checkout API error:', error);
        
        // Handle active subscription exists error
        if (error.error === 'ACTIVE_SUBSCRIPTION_EXISTS' && error.subscription) {
          setActiveSubscriptionInfo(error.subscription);
          setIsActiveSubModalOpen(true);
          
          // Also refresh subscription state to sync UI
          refreshSubscription().catch(console.error);
          return;
        }
        
        alert(`Checkout failed: ${error.error || 'Unknown error'}`);
        return;
      }
      
      const data = await response.json();
      console.log('[UpgradeModal] Checkout response:', data);
      
      // Check if this was an upgrade/downgrade (no checkout URL means subscription was updated directly)
      if (data.success && (data.message === 'Subscription updated successfully' || data.message === 'Subscription will be canceled at period end')) {
        // Upgrade/downgrade successful - show success modal
        const oldPlanName = plans.find(p => p.id === activePlan)?.name || activePlan;
        const newPlanName = plans.find(p => p.id === selectedPlan)?.name || selectedPlan;
        
        setUpgradeInfo({ oldPlan: oldPlanName, newPlan: newPlanName });
        setIsUpgradeSuccessOpen(true);
        
        // Refresh subscription to update UI
        await refreshSubscription();
        
        return;
      }
      
      // Redirect to Clerk's checkout page for new subscriptions
      if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        console.error('[UpgradeModal] No checkout URL in response');
      }
      
    } catch (error) {
      console.error('[UpgradeModal] Checkout error:', error);
      alert('Failed to start checkout. Please try again.');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleCancelSubscription = async () => {
    try {
      const token = await getToken();
      if (!token) {
        alert('Authentication required. Please sign in again.');
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/billing/cancel`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to cancel subscription');
      }

      // Store the plan name for the success modal
      const planName = plans.find(p => p.id === activePlan)?.name || activePlan;
      setCancelledPlanName(planName);
      
      // Refresh subscription state to get updated cancelAtPeriodEnd flag
      await refreshSubscription();
      
      // Close the cancel confirmation modal and show success modal
      setIsCancelModalOpen(false);
      setIsCancelSuccessOpen(true);
    } catch (error) {
      console.error('[UpgradeModal] Cancel error:', error);
      alert('Failed to cancel subscription. Please try again or contact support.');
    }
  };

  const handleResubscribe = async () => {
    try {
      setIsProcessing(true);
      const token = await getToken();
      if (!token) {
        alert('Authentication required. Please sign in again.');
        return;
      }

      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
      const response = await fetch(`${apiUrl}/api/billing/resubscribe`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to reactivate subscription');
      }

      const data = await response.json();
      console.log('[UpgradeModal] Resubscribe response:', data);

      // Get plan name for success modal
      const planName = plans.find(p => p.id === activePlan)?.name || activePlan;
      
      // Show success modal with resubscribe message
      setUpgradeInfo({ oldPlan: 'Cancelled', newPlan: planName });
      setIsUpgradeSuccessOpen(true);

      // Refresh subscription to update UI
      await refreshSubscription();
    } catch (error) {
      console.error('[UpgradeModal] Resubscribe error:', error);
      alert('Failed to reactivate subscription. Please try again or contact support.');
    } finally {
      setIsProcessing(false);
    }
  };

  const getYearlySavings = (plan: PlanDetails): number => {
    const monthlyCost = plan.price * 12;
    const yearlyCost = plan.priceYearly || (plan.price * 12);
    return Math.round(monthlyCost - yearlyCost);
  };

  const renderPlanCard = (plan: PlanDetails) => {
    const isCurrentPlan = plan.id === activePlan;
    const isSelected = plan.id === selectedPlan;
    const canSelect = canChangeTo(plan.id as SubscriptionPlan);
    
    // Debug logging
    if (isCurrentPlan) {
      console.log('[UpgradeModal] Current plan card:', {
        planId: plan.id,
        isCurrentPlan,
        cancelAtPeriodEnd,
        shouldShowResubscribe: cancelAtPeriodEnd === true
      });
    }
    const isUpgrade = canUpgradeTo(plan.id as SubscriptionPlan);
    const isDowngrade = !isUpgrade && canSelect;
    const isHighlighted = plan.popular || plan.id === highlightPlan;

    return (
      <div
        key={plan.id}
        onClick={() => handleSelectPlan(plan.id as SubscriptionPlan)}
        className={`
          relative flex flex-col p-4 sm:p-6 rounded-2xl border-2 transition-all duration-300 cursor-pointer
          ${isSelected 
            ? 'border-blue-500 bg-blue-500/10 shadow-lg shadow-blue-500/20 scale-[1.02]' 
            : isHighlighted 
              ? 'border-indigo-500/50 bg-gradient-to-b from-indigo-500/10 to-transparent' 
              : 'border-slate-700/50 bg-slate-800/30 hover:border-slate-600'
          }
          ${!canSelect && !isCurrentPlan ? 'opacity-50 cursor-not-allowed' : ''}
          ${isCurrentPlan ? 'border-green-500/50 bg-green-500/5' : ''}
        `}
      >
        {/* Badge */}
        {plan.badge && (
          <div className="absolute -top-3 left-1/2 -translate-x-1/2">
            <span className="px-3 py-1 text-xs font-bold text-white bg-gradient-to-r from-indigo-500 to-purple-500 rounded-full shadow-lg">
              {plan.badge}
            </span>
          </div>
        )}

        {/* Current Plan Badge */}
        {isCurrentPlan && (
          <div className="absolute -top-3 right-4">
            <span className="px-3 py-1 text-xs font-semibold text-green-400 bg-green-500/20 border border-green-500/30 rounded-full">
              Current Plan
            </span>
          </div>
        )}

        {/* Plan Name */}
        <h3 className="text-lg sm:text-xl font-bold text-white mb-1">{plan.name}</h3>
        <p className="text-sm text-slate-400 mb-4">{plan.description}</p>

        {/* Price */}
        <div className="mb-4">
          {plan.price === 0 ? (
            <div className="text-3xl sm:text-4xl font-bold text-white">Free</div>
          ) : (
            <>
              <div className="flex items-baseline gap-1">
                <span className="text-3xl sm:text-4xl font-bold text-white">
                  ${billingPeriod === 'monthly' ? plan.price : ((plan.priceYearly || plan.price * 12) / 12).toFixed(2)}
                </span>
                <span className="text-slate-400">/mo</span>
              </div>
              {billingPeriod === 'yearly' && (
                <div className="mt-1 text-sm text-green-400">
                  Save ${getYearlySavings(plan)}/year
                </div>
              )}
            </>
          )}
        </div>

        {/* Features */}
        <ul className="space-y-2 flex-1">
          {plan.features.slice(0, 5).map((feature, idx) => (
            <li key={idx} className="flex items-start gap-2 text-sm text-slate-300">
              <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              <span>{feature.name}</span>
            </li>
          ))}
          {plan.features.length > 5 && (
            <li className="text-sm text-slate-500">+{plan.features.length - 5} more features</li>
          )}
        </ul>

        {/* Select Button */}
        {isCurrentPlan && plan.id !== 'free' ? (
          cancelAtPeriodEnd ? (
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleResubscribe();
              }}
              disabled={isProcessing}
              className="mt-4 w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all bg-green-600 hover:bg-green-500 text-white disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? 'Processing...' : 'Resubscribe'}
            </button>
          ) : (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setIsCancelModalOpen(true);
              }}
              className="mt-4 w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all bg-red-600 hover:bg-red-500 text-white"
            >
              Cancel Plan
            </button>
          )
        ) : (
          <button
            disabled={!canSelect || isCurrentPlan}
            className={`
              mt-4 w-full py-2.5 sm:py-3 rounded-xl font-semibold text-sm sm:text-base transition-all
              ${isSelected
                ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white shadow-lg shadow-blue-500/30'
                : isCurrentPlan
                  ? 'bg-slate-700 text-slate-400 cursor-not-allowed'
                  : canSelect
                    ? isDowngrade 
                      ? 'bg-slate-700 hover:bg-slate-600 text-white border border-orange-500/30'
                      : 'bg-slate-700 hover:bg-slate-600 text-white'
                    : 'bg-slate-800 text-slate-500 cursor-not-allowed'
              }
            `}
          >
            {isCurrentPlan 
              ? 'Current Plan' 
              : isSelected 
                ? 'Selected' 
                : canSelect 
                  ? isDowngrade 
                    ? 'Downgrade to this Plan' 
                    : 'Upgrade to this Plan'
                  : 'Included'}
          </button>
        )}
      </div>
    );
  };

  const renderPlansStep = () => (
    <div className="space-y-6">
      {/* Billing Toggle */}
      <div className="flex items-center justify-center gap-4">
        <span className={`text-sm ${billingPeriod === 'monthly' ? 'text-white' : 'text-slate-400'}`}>Monthly</span>
        <button
          onClick={() => setBillingPeriod(billingPeriod === 'monthly' ? 'yearly' : 'monthly')}
          className={`
            relative w-14 h-7 rounded-full transition-colors
            ${billingPeriod === 'yearly' ? 'bg-gradient-to-r from-green-500 to-emerald-500' : 'bg-slate-700'}
          `}
        >
          <div
            className={`
              absolute top-1 w-5 h-5 bg-white rounded-full shadow-md transition-transform
              ${billingPeriod === 'yearly' ? 'translate-x-8' : 'translate-x-1'}
            `}
          />
        </button>
        <span className={`text-sm ${billingPeriod === 'yearly' ? 'text-white' : 'text-slate-400'}`}>
          Yearly
          <span className="ml-1 text-green-400 text-xs font-semibold">Save 13%</span>
        </span>
      </div>

      {/* Plans Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {isPlansLoading ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12 space-y-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <p className="text-slate-400 text-sm">Loading subscription plans...</p>
          </div>
        ) : plans.length === 0 ? (
          <div className="col-span-full flex flex-col items-center justify-center py-12 space-y-4">
            <div className="text-red-400 text-4xl">⚠️</div>
            <div className="text-center">
              <p className="text-white font-semibold mb-2">Unable to Load Plans</p>
              <p className="text-slate-400 text-sm mb-4">
                Could not connect to the billing service. Please ensure the backend is running.
              </p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm font-medium transition-colors"
              >
                Retry
              </button>
            </div>
          </div>
        ) : (
          plans.map(renderPlanCard)
        )}
      </div>
    </div>
  );

  const renderFeaturesStep = () => {
    const selectedPlanDetails = plans.find(p => p.id === selectedPlan);
    
    // Get features and ensure they exist
    const features = selectedPlanDetails?.features || [];
    const visibleFeatures = features.filter(feature => feature.visible !== false);

    // Debug logging
    console.log('[UpgradeModal] Features Step Debug:', {
      selectedPlan,
      selectedPlanDetails: selectedPlanDetails?.name,
      featuresCount: features.length,
      visibleFeaturesCount: visibleFeatures.length,
      features: features,
      allPlans: plans.map(p => ({ id: p.id, name: p.name, featuresCount: p.features?.length }))
    });

    return (
      <div className="space-y-6">
        {/* Selected Plan Summary */}
        <div className="bg-gradient-to-r from-blue-500/10 to-indigo-500/10 border border-blue-500/30 rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-2xl font-bold text-white">{selectedPlanDetails?.name} Plan</h3>
              <p className="text-slate-400">{selectedPlanDetails?.description}</p>
            </div>
            <div className="text-right">
              <div className="text-3xl font-bold text-white">
                ${billingPeriod === 'monthly' 
                  ? selectedPlanDetails?.price 
                  : ((selectedPlanDetails?.priceYearly || 0) / 12).toFixed(2)
                }
                <span className="text-lg text-slate-400">/mo</span>
              </div>
              {billingPeriod === 'yearly' && selectedPlanDetails && (
                <div className="text-sm text-green-400">
                  Billed ${selectedPlanDetails.priceYearly}/year
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Feature List from Selected Plan */}
        <div className="bg-slate-800/50 rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-slate-700">
            <h4 className="text-lg font-semibold text-white">What you'll get</h4>
          </div>
          <div className="divide-y divide-slate-700/50">
            {visibleFeatures.length > 0 ? (
              visibleFeatures.map((feature, idx) => {
                const isIncluded = feature.included !== false;

                return (
                  <div key={idx} className="flex items-start gap-3 p-4 hover:bg-slate-700/30 transition-colors">
                    {isIncluded ? (
                      <svg className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    )}
                    <div className="flex-1">
                      <span className={`${isIncluded ? 'text-slate-200' : 'text-slate-500'}`}>
                        {feature.name}
                      </span>
                      {feature.limit && (
                        <span className="text-xs text-slate-400 mt-1 block">Limit: {feature.limit}</span>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-8 text-center text-slate-400">
                <p>Loading features...</p>
                {selectedPlan && (
                  <p className="text-sm mt-2">Selected plan: {selectedPlan}</p>
                )}
                {selectedPlanDetails && (
                  <p className="text-sm mt-1">Features count: {features.length}</p>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderCheckoutStep = () => {
    const selectedPlanDetails = plans.find(p => p.id === selectedPlan);
    const price = billingPeriod === 'monthly' 
      ? selectedPlanDetails?.price || 0
      : (selectedPlanDetails?.priceYearly || 0) / 12;
    const total = billingPeriod === 'monthly' 
      ? selectedPlanDetails?.price || 0
      : selectedPlanDetails?.priceYearly || 0;

    return (
      <div className="max-w-md mx-auto space-y-6">
        {/* Test Mode Banner - Only show if backend confirms test mode */}
        {checkoutInfo?.isTestMode && (
          <div className="bg-yellow-500/10 border-2 border-yellow-500/50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="text-yellow-500 text-2xl">⚠️</div>
              <div className="flex-1">
                <h4 className="text-yellow-400 font-bold mb-2">Test Mode - Use Test Card</h4>
                <div className="text-sm text-slate-300 space-y-2">
                  <p className="font-semibold">On the next page, use this test card:</p>
                  <div className="bg-slate-900/50 rounded-lg p-3 font-mono text-xs space-y-1">
                    <div><span className="text-slate-500">Card:</span> <span className="text-white font-bold">4242 4242 4242 4242</span></div>
                    <div><span className="text-slate-500">Expiry:</span> <span className="text-white">Any future date (e.g., 12/34)</span></div>
                    <div><span className="text-slate-500">CVC:</span> <span className="text-white">Any 3 digits (e.g., 123)</span></div>
                    <div><span className="text-slate-500">ZIP:</span> <span className="text-white">Any 5 digits (e.g., 12345)</span></div>
                  </div>
                  <p className="text-yellow-400/80 text-xs mt-2">
                    💡 This is a test environment. No real charges will be made.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Trial Period Reassurance Banner - Show for all users with trial */}
        {checkoutInfo?.trialPeriodDays && checkoutInfo.trialPeriodDays > 0 && (
          <div className="bg-green-500/10 border-2 border-green-500/50 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <div className="text-green-500 text-2xl">🛡️</div>
              <div className="flex-1">
                <h4 className="text-green-400 font-bold mb-2">Free {checkoutInfo.trialPeriodDays}-Day Trial</h4>
                <div className="text-sm text-slate-300 space-y-2">
                  <p className="font-semibold">Your card will NOT be charged today!</p>
                  <ul className="space-y-1 text-xs">
                    <li>✓ Enter your payment details securely</li>
                    <li>✓ Start your {checkoutInfo.trialPeriodDays}-day free trial immediately</li>
                    <li>✓ No charge until {new Date(Date.now() + checkoutInfo.trialPeriodDays * 24 * 60 * 60 * 1000).toLocaleDateString()}</li>
                    <li>✓ Cancel anytime during trial - no questions asked</li>
                  </ul>
                  <p className="text-green-400/80 text-xs mt-2 font-semibold">
                    💳 Your first charge happens only after the trial period ends.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Discount Code Section */}
        <div className="bg-slate-800/50 rounded-2xl p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold text-white">Discount Code</h4>
            {influencer && discountCode && (
              <span className="text-xs text-blue-400">
                From {influencer.name}
              </span>
            )}
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={discountCode}
              onChange={(e) => {
                setDiscountCode(e.target.value.toUpperCase());
                setDiscountInfo(null);
              }}
              placeholder="Enter discount code"
              className="flex-1 px-3 py-2 bg-slate-900/50 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:border-blue-500 transition-colors text-sm"
            />
            <button
              onClick={async () => {
                if (!discountCode.trim()) return;
                setIsValidatingDiscount(true);
                try {
                  const token = await getToken();
                  if (!token) return;
                  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000';
                  const response = await fetch(`${apiUrl}/api/billing/validate-discount`, {
                    method: 'POST',
                    headers: {
                      'Authorization': `Bearer ${token}`,
                      'Content-Type': 'application/json',
                    },
                    body: JSON.stringify({ code: discountCode }),
                  });
                  const data = await response.json();
                  setDiscountInfo(data);
                } catch (error) {
                  console.error('[UpgradeModal] Failed to validate discount:', error);
                  setDiscountInfo({ valid: false, code: discountCode, name: '', percentOff: null, amountOff: null, error: 'Failed to validate code' });
                } finally {
                  setIsValidatingDiscount(false);
                }
              }}
              disabled={isValidatingDiscount || !discountCode.trim()}
              className="px-4 py-2 bg-slate-700 hover:bg-slate-600 disabled:bg-slate-800 disabled:text-slate-500 text-white rounded-lg text-sm font-medium transition-colors"
            >
              {isValidatingDiscount ? 'Checking...' : 'Apply'}
            </button>
          </div>
          {discountInfo && (
            <div className={`text-sm ${discountInfo.valid ? 'text-green-400' : 'text-red-400'}`}>
              {discountInfo.valid ? (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>
                    {discountInfo.name}: {discountInfo.percentOff ? `${discountInfo.percentOff}% off` : `$${discountInfo.amountOff} off`}
                    {discountInfo.duration === 'once' && ' (first payment)'}
                    {discountInfo.duration === 'repeating' && discountInfo.durationInMonths && ` (${discountInfo.durationInMonths} months)`}
                    {discountInfo.duration === 'forever' && ' (forever)'}
                  </span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  <span>{discountInfo.error || 'Invalid discount code'}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-slate-800/50 rounded-2xl p-6 space-y-4">
          <h4 className="text-lg font-semibold text-white">Order Summary</h4>
          
          <div className="space-y-3">
            <div className="flex justify-between text-slate-300">
              <span>{selectedPlanDetails?.name} Plan</span>
              <span>${price.toFixed(2)}/mo</span>
            </div>
            <div className="flex justify-between text-slate-400 text-sm">
              <span>Billing Period</span>
              <span className="capitalize">{billingPeriod}</span>
            </div>
            {billingPeriod === 'yearly' && selectedPlanDetails && (
              <div className="flex justify-between text-green-400 text-sm">
                <span>Annual Savings</span>
                <span>-${getYearlySavings(selectedPlanDetails)}</span>
              </div>
            )}
            {/* Show discount if valid code applied */}
            {discountInfo?.valid && (
              <div className="flex justify-between text-green-400 text-sm">
                <span>Discount ({discountInfo.name})</span>
                <span>
                  -{discountInfo.percentOff 
                    ? `${discountInfo.percentOff}%` 
                    : `$${discountInfo.amountOff?.toFixed(2)}`}
                </span>
              </div>
            )}
          </div>

          <div className="border-t border-slate-700 pt-4">
            {/* Show discounted total if discount applied */}
            {discountInfo?.valid ? (
              <div className="space-y-2">
                <div className="flex justify-between text-slate-400 text-sm line-through">
                  <span>Original Price</span>
                  <span>${total.toFixed(2)}{billingPeriod === 'yearly' ? '/yr' : '/mo'}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-green-400">
                  <span>
                    After Discount {billingPeriod === 'yearly' ? '(billed annually)' : ''}
                    {discountInfo.duration === 'once' && <span className="text-xs font-normal text-slate-400 ml-1">(first payment)</span>}
                  </span>
                  <span>
                    ${(discountInfo.percentOff 
                      ? total * (1 - discountInfo.percentOff / 100)
                      : total - (discountInfo.amountOff || 0)
                    ).toFixed(2)}{billingPeriod === 'yearly' ? '/yr' : '/mo'}
                  </span>
                </div>
              </div>
            ) : (
              <div className="flex justify-between text-lg font-bold text-white">
                <span>Total {billingPeriod === 'yearly' ? '(billed annually)' : ''}</span>
                <span>${total.toFixed(2)}{billingPeriod === 'yearly' ? '/yr' : '/mo'}</span>
              </div>
            )}
          </div>
        </div>

        {/* Discount Note - Stripe doesn't show discounted price clearly with trials */}
        {discountInfo?.valid && checkoutInfo?.trialPeriodDays && checkoutInfo.trialPeriodDays > 0 && (
          <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-3 text-sm text-blue-300">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>
                <strong>Note:</strong> Stripe's checkout page may show the original price. Your discount code <strong>{discountInfo.code}</strong> is applied and you'll be charged the discounted price shown above after your trial ends.
              </span>
            </div>
          </div>
        )}

        {/* Security Badge */}
        <div className="flex items-center justify-center gap-2 text-slate-400 text-sm">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
          </svg>
          <span>Secure checkout powered by Stripe</span>
        </div>

        {/* Checkout Button */}
        <button
          onClick={handleCheckout}
          disabled={isProcessing}
          className="w-full py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl text-white font-bold text-lg shadow-lg shadow-blue-500/30 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isProcessing ? (
            <span className="flex items-center justify-center gap-2">
              <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
              Processing...
            </span>
          ) : (
            'Complete Upgrade'
          )}
        </button>

        {/* Terms */}
        <p className="text-xs text-slate-500 text-center">
          By completing this purchase, you agree to our Terms of Service and Privacy Policy.
          You can cancel anytime from your account settings.
        </p>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative w-full max-w-5xl max-h-[90vh] overflow-y-auto bg-gradient-to-b from-slate-900 to-slate-950 border border-slate-700/50 rounded-3xl shadow-2xl">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-slate-900/95 backdrop-blur-sm border-b border-slate-700/50 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white">
                {currentStep === 'plans' && 'Upgrade Your Plan'}
                {currentStep === 'features' && 'Review Features'}
                {currentStep === 'checkout' && 'Complete Your Upgrade'}
              </h2>
              {requiredFeature && currentStep === 'plans' && (
                <p className="text-slate-400 mt-1">{getFeatureMessage()}</p>
              )}
            </div>
            <button
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center justify-center gap-2 mt-4">
            {['plans', 'features', 'checkout'].map((step, idx) => (
              <React.Fragment key={step}>
                <div
                  className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold transition-colors
                    ${currentStep === step 
                      ? 'bg-blue-500 text-white' 
                      : idx < ['plans', 'features', 'checkout'].indexOf(currentStep)
                        ? 'bg-green-500 text-white'
                        : 'bg-slate-700 text-slate-400'
                    }
                  `}
                >
                  {idx < ['plans', 'features', 'checkout'].indexOf(currentStep) ? (
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  ) : (
                    idx + 1
                  )}
                </div>
                {idx < 2 && (
                  <div className={`w-12 h-0.5 ${idx < ['plans', 'features', 'checkout'].indexOf(currentStep) ? 'bg-green-500' : 'bg-slate-700'}`} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-4 sm:p-6">
          {currentStep === 'plans' && renderPlansStep()}
          {currentStep === 'features' && renderFeaturesStep()}
          {currentStep === 'checkout' && renderCheckoutStep()}
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-slate-900/95 backdrop-blur-sm border-t border-slate-700/50 p-4 sm:p-6">
          <div className="flex items-center justify-between">
            {currentStep !== 'plans' ? (
              <button
                onClick={handleBack}
                className="px-6 py-2.5 text-slate-300 hover:text-white hover:bg-slate-800 rounded-xl transition-colors"
              >
                Back
              </button>
            ) : (
              <div />
            )}

            {currentStep !== 'checkout' && (
              <button
                onClick={handleContinue}
                disabled={!selectedPlan || selectedPlan === activePlan}
                className="px-8 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 rounded-xl text-white font-semibold shadow-lg shadow-blue-500/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Cancel Subscription Modal */}
      <CancelSubscriptionModal
        isOpen={isCancelModalOpen}
        onClose={() => setIsCancelModalOpen(false)}
        onConfirmCancel={handleCancelSubscription}
        currentPlan={activePlan}
        planFeatures={plans.find(p => p.id === activePlan)?.features || []}
      />

      {/* Cancel Success Modal */}
      <CancelSuccessModal
        isOpen={isCancelSuccessOpen}
        onClose={() => {
          setIsCancelSuccessOpen(false);
          onClose();
        }}
        previousPlan={cancelledPlanName}
        subscriptionEndDate={expiresAt}
      />

      {/* Active Subscription Modal */}
      <ActiveSubscriptionModal
        isOpen={isActiveSubModalOpen}
        onClose={() => {
          setIsActiveSubModalOpen(false);
          onClose();
        }}
        subscription={activeSubscriptionInfo}
      />

      {/* Upgrade Success Modal */}
      {upgradeInfo && (
        <UpgradeSuccessModal
          isOpen={isUpgradeSuccessOpen}
          onClose={() => {
            setIsUpgradeSuccessOpen(false);
            onClose();
          }}
          oldPlan={upgradeInfo.oldPlan}
          newPlan={upgradeInfo.newPlan}
        />
      )}
    </div>
  );
};

export default UpgradeModal;
